/**
 * Simple linear regression by ordinary least squares: ŷ = a + bx.
 *
 * b = Sxy / Sxx and a = ȳ − b·x̄, with the standard inference that assumes independent
 * errors with constant variance, normally distributed: s = √(SSE / (n − 2)),
 * SE(b) = s / √Sxx, SE(a) = s√(1/n + x̄²/Sxx), t-tests against zero with n − 2 df, and
 * the ANOVA F = MSR / MSE (which equals the slope's t²). Checked against SciPy's
 * `linregress`.
 */

import { mean } from "@/lib/stats/descriptive";
import {
  fUpper,
  studentTQuantile,
  studentTTwoTailed,
} from "@/lib/stats/distributions";

export interface ResidualRow {
  x: number;
  y: number;
  fitted: number;
  residual: number;
}

export interface RegressionReport {
  n: number;
  meanX: number;
  meanY: number;
  sxx: number;
  syy: number;
  sxy: number;
  slope: number;
  intercept: number;
  r: number;
  rSquared: number;
  /** Σ(y − ŷ)². */
  sse: number;
  /** Σ(ŷ − ȳ)². */
  ssr: number;
  /** Σ(y − ȳ)². */
  sst: number;
  dfRegression: 1;
  dfResidual: number;
  msr: number;
  mse: number;
  f: number;
  pF: number;
  /** Standard error of the estimate, √MSE. */
  see: number;
  seSlope: number;
  seIntercept: number;
  tSlope: number;
  pSlope: number;
  tIntercept: number;
  pIntercept: number;
  /** t* for a two-sided 95 % interval with n − 2 df. */
  tCritical: number;
  slopeCi: [number, number];
  interceptCi: [number, number];
  residuals: ResidualRow[];
  minX: number;
  maxX: number;
}

function tStatistic(estimate: number, se: number): number {
  if (se > 0) {
    return estimate / se;
  }
  if (estimate === 0) {
    return Number.NaN;
  }
  return estimate > 0 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
}

function twoSidedP(t: number, df: number): number {
  if (Number.isNaN(t)) {
    return Number.NaN;
  }
  return Number.isFinite(t) ? studentTTwoTailed(t, df) : 0;
}

function sums(xs: readonly number[], ys: readonly number[]) {
  const meanX = mean(xs);
  const meanY = mean(ys);
  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  let minX = xs[0];
  let maxX = xs[0];
  for (let i = 0; i < xs.length; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
    minX = Math.min(minX, xs[i]);
    maxX = Math.max(maxX, xs[i]);
  }
  return { meanX, meanY, sxx, syy, sxy, minX, maxX };
}

export function linearRegression(
  xs: readonly number[],
  ys: readonly number[]
): RegressionReport | null {
  const n = xs.length;
  if (n < 3 || ys.length !== n) {
    return null;
  }
  const { meanX, meanY, sxx, syy, sxy, minX, maxX } = sums(xs, ys);
  if (sxx === 0) {
    return null;
  }
  const slope = sxy / sxx;
  const intercept = meanY - slope * meanX;
  const residuals = xs.map((x, i) => {
    const fitted = intercept + slope * x;
    return { x, y: ys[i], fitted, residual: ys[i] - fitted };
  });
  let sse = 0;
  for (const row of residuals) {
    sse += row.residual * row.residual;
  }
  const dfResidual = n - 2;
  const ssr = (sxy * sxy) / sxx;
  const mse = sse / dfResidual;
  const see = Math.sqrt(mse);
  const seSlope = see / Math.sqrt(sxx);
  const seIntercept = see * Math.sqrt(1 / n + (meanX * meanX) / sxx);
  const tSlope = tStatistic(slope, seSlope);
  const tIntercept = tStatistic(intercept, seIntercept);
  const tCritical = studentTQuantile(0.975, dfResidual);
  const f = mse > 0 ? ssr / mse : Number.POSITIVE_INFINITY;
  const r = syy > 0 ? sxy / Math.sqrt(sxx * syy) : Number.NaN;
  return {
    n,
    meanX,
    meanY,
    sxx,
    syy,
    sxy,
    slope,
    intercept,
    r,
    rSquared: r * r,
    sse,
    ssr,
    sst: syy,
    dfRegression: 1,
    dfResidual,
    msr: ssr,
    mse,
    f,
    pF: Number.isFinite(f) ? fUpper(f, 1, dfResidual) : 0,
    see,
    seSlope,
    seIntercept,
    tSlope,
    pSlope: twoSidedP(tSlope, dfResidual),
    tIntercept,
    pIntercept: twoSidedP(tIntercept, dfResidual),
    tCritical,
    slopeCi: [slope - tCritical * seSlope, slope + tCritical * seSlope],
    interceptCi: [
      intercept - tCritical * seIntercept,
      intercept + tCritical * seIntercept,
    ],
    residuals,
    minX,
    maxX,
  };
}

export interface Prediction {
  x: number;
  fitted: number;
  /** SE of the fitted mean response at x. */
  seMean: number;
  /** SE for a single new observation at x. */
  sePrediction: number;
  /** 95 % confidence interval for the mean of y at x. */
  confidence: [number, number];
  /** 95 % prediction interval for one new y at x. */
  prediction: [number, number];
  /** x lies outside the observed range of x. */
  extrapolated: boolean;
}

export function predictAt(report: RegressionReport, x: number): Prediction {
  const fitted = report.intercept + report.slope * x;
  const leverage = 1 / report.n + (x - report.meanX) ** 2 / report.sxx;
  const seMean = report.see * Math.sqrt(leverage);
  const sePrediction = report.see * Math.sqrt(1 + leverage);
  const t = report.tCritical;
  return {
    x,
    fitted,
    seMean,
    sePrediction,
    confidence: [fitted - t * seMean, fitted + t * seMean],
    prediction: [fitted - t * sePrediction, fitted + t * sePrediction],
    extrapolated: x < report.minX || x > report.maxX,
  };
}
